// ClinicQueueCard — surfaces live waiting-room status for in-clinic
// (sessionMode='OFFLINE') booked events scheduled for today.
//
// Renders only for offline sessions that haven't started yet (status
// SCHEDULED, scheduledAt today). Multiple cards stack at the top of Today
// when the patient has more than one in-clinic appointment the same day.
//
// Each card shows:
//   - Patient's appointment number (e.g. "You're #5")
//   - Currently being seen (e.g. "Now serving #3")
//   - Estimated wait (derived from queue gap × average duration)
//   - Chamber location + scheduled time + practitioner
//
// Polls /clinic-queue/{eventId} every 30s while mounted. A follow-up phase
// will replace polling with Agora signal pushes — same component contract,
// the hook just stops re-fetching on a timer.

import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import WidgetCard from './WidgetCard'
import { GhostButton } from '../../../layout/styledComponents'
import { usePatient } from '../../../context/PatientContext'
import { getQueueStatus } from '../../../services/clinicQueueService'
import { parseUtcDate } from '../../../utils/dates'

const POLL_INTERVAL_MS = 30_000

function isToday(iso) {
  if (!iso) return false
  const d = parseUtcDate(iso)
  const now = new Date()
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  )
}

function isBeforeNow(iso) {
  if (!iso) return false
  return parseUtcDate(iso).getTime() < Date.now()
}

function formatTime(iso) {
  if (!iso) return ''
  return parseUtcDate(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

function formatWait(minutes, yourTurnNow) {
  if (yourTurnNow) return 'It’s your turn — head in.'
  if (minutes == null) return ''
  if (minutes <= 0) return 'You’re next — please be ready.'
  if (minutes < 60) return `~${minutes} min wait`
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (m === 0) return `~${h}h wait`
  return `~${h}h ${m}m wait`
}

const Layout = styled.div`
  display: grid;
  gap: 18px;
  grid-template-columns: 1fr 1fr;
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const Stat = styled.div`
  background: ${(p) => p.theme?.color?.bgSubtle || '#F4F0E9'};
  border: 1px solid ${(p) => p.theme?.color?.border || '#E6E0D6'};
  border-radius: 14px;
  padding: 14px 16px;
`

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-bottom: 6px;
`

const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${(p) => (p.$accent ? p.theme?.color?.primary || '#1F3A5F' : p.theme?.color?.ink || '#1F2937')};
  letter-spacing: -0.02em;
  line-height: 1;
`

const StatHint = styled.div`
  font-size: 12px;
  color: ${(p) => p.theme?.color?.inkMuted || '#6B7280'};
  margin-top: 6px;
`

const Meta = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
  font-size: 13px;
  color: ${(p) => p.theme?.color?.inkSoft || '#4B5563'};
`

const Wait = styled.div`
  margin-top: 16px;
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => p.theme?.color?.accent || '#6B9080'};
`

const Actions = styled.div`
  margin-top: 14px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

function QueueCard({ session }) {
  const navigate = useNavigate()
  const { currentProfile } = usePatient()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const refresh = useCallback(
    async (signal) => {
      if (!currentProfile || !session?.eventId) return
      try {
        const data = await getQueueStatus(currentProfile, session.eventId)
        if (signal && signal.aborted) return
        setStatus(data)
        setError(null)
      } catch (e) {
        if (signal && signal.aborted) return
        setError(e?.message || 'Could not refresh queue status.')
      } finally {
        if (!signal || !signal.aborted) setLoading(false)
      }
    },
    [currentProfile, session?.eventId],
  )

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    refresh(ctrl.signal)
    const id = setInterval(() => refresh(ctrl.signal), POLL_INTERVAL_MS)
    return () => {
      ctrl.abort()
      clearInterval(id)
    }
  }, [refresh])

  if (loading && !status) {
    return (
      <WidgetCard
        tone="success"
        title="In-clinic queue"
        badge={session.title || 'In-clinic appointment'}
      >
        <StatHint>Checking your queue position…</StatHint>
      </WidgetCard>
    )
  }

  if (!status) {
    return (
      <WidgetCard
        tone="success"
        title="In-clinic queue"
        badge={session.title || 'In-clinic appointment'}
      >
        <StatHint>{error || 'Queue status not available right now.'}</StatHint>
        <Actions>
          <GhostButton onClick={() => navigate(`/patient-home/sessions/${session.eventId}`)}>
            View session
          </GhostButton>
        </Actions>
      </WidgetCard>
    )
  }

  const yourNumber = status.yourAppointmentNumber
  const currentNumber = status.currentAppointmentNumber
  // People physically waiting ahead of the patient: every slot strictly between
  // the one being seen and theirs. The slot being seen isn't "ahead" — it's
  // happening now — so the gap is `your - current - 1`, floored at 0.
  const peopleAhead =
    yourNumber != null && currentNumber != null
      ? Math.max(0, yourNumber - currentNumber - 1)
      : null
  const yourTurnNow = currentNumber != null && yourNumber === currentNumber

  return (
    <WidgetCard
      tone="success"
      title="In-clinic queue"
      badge={session.title || 'In-clinic appointment'}
      pulse={currentNumber != null && yourNumber != null && yourNumber - currentNumber <= 1}
    >
      <Layout>
        <Stat>
          <StatLabel>You&apos;re number</StatLabel>
          <StatValue $accent>#{yourNumber}</StatValue>
          <StatHint>
            {status.totalAppointmentsToday
              ? `of ${status.totalAppointmentsToday} today`
              : 'in today’s queue'}
          </StatHint>
        </Stat>
        <Stat>
          <StatLabel>Now serving</StatLabel>
          <StatValue>
            {currentNumber == null ? '—' : `#${currentNumber}`}
          </StatValue>
          <StatHint>
            {currentNumber == null
              ? 'Clinic hasn’t started yet'
              : yourTurnNow
                ? 'You’re being called'
                : peopleAhead === 0
                  ? 'You’re next'
                  : `${peopleAhead} ${peopleAhead === 1 ? 'person' : 'people'} ahead`}
          </StatHint>
        </Stat>
      </Layout>

      <Wait>{formatWait(status.estimatedWaitMinutes, yourTurnNow)}</Wait>

      <Meta>
        {session.practitionerName && <span>👩‍⚕️ {session.practitionerName}</span>}
        {(session.locationName || status.chamberRoomName) && (
          <span>📍 {session.locationName || status.chamberRoomName}</span>
        )}
        {session.scheduledAt && (
          <span>🕐 Scheduled for {formatTime(session.scheduledAt)}</span>
        )}
      </Meta>

      <Actions>
        <GhostButton onClick={() => navigate(`/patient-home/sessions/${session.eventId}`)}>
          View session
        </GhostButton>
      </Actions>
    </WidgetCard>
  )
}

/**
 * Lists an in-clinic queue card per offline session scheduled for today
 * that hasn't started yet. Source: union of {@code nextSession} and
 * {@code upcomingSessions} filtered to {@code sessionMode === 'OFFLINE'},
 * scheduled today, status SCHEDULED, scheduled time not yet elapsed.
 */
export default function ClinicQueueCard({ nextSession, upcomingSessions }) {
  const seen = new Set()
  const offline = []
  const collect = (s) => {
    if (!s || !s.eventId) return
    if (seen.has(s.eventId)) return
    if (s.sessionMode !== 'OFFLINE') return
    if (!isToday(s.scheduledAt)) return
    if (s.status && s.status !== 'SCHEDULED') return
    // Hide once the slot has elapsed — patient is presumably checked-in /
    // already being seen and the queue card is no longer informative.
    if (isBeforeNow(s.scheduledAt) && s.canJoinNow !== true) {
      const minutesPast = (Date.now() - parseUtcDate(s.scheduledAt).getTime()) / 60000
      if (minutesPast > 5) return
    }
    seen.add(s.eventId)
    offline.push(s)
  }
  collect(nextSession)
  ;(upcomingSessions || []).forEach(collect)

  if (offline.length === 0) return null

  offline.sort((a, b) => parseUtcDate(a.scheduledAt).getTime() - parseUtcDate(b.scheduledAt).getTime())

  return (
    <>
      {offline.map((s) => (
        <QueueCard key={s.eventId} session={s} />
      ))}
    </>
  )
}
